import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouterHooks } from "../../../../withRouters/withRoutersHook";
import { connect } from "react-redux";
import { Loader2, ArrowLeft, X, Send, RefreshCw } from "lucide-react";
import DropdownField from "../../FileManager/DropdownField";
import {
  GetIndividualAIResponse,
  GenerateAIResponse,
  resetIsAIResponseGenerated,
  EditAIResponse,
  SendMessageAction,
  addMessageToRedux,
} from "../../../../Actions/Messaging";

class CustomizeAIResponse extends Component {
  state = {
    tone: "Neutral",
    length: "Medium",
    isGeneratingResponse: false,
    editError: null,
    sendError: null,
  };

  static propTypes = {
    GetIndividualAIResponse: PropTypes.func.isRequired,
    GenerateAIResponse: PropTypes.func.isRequired,
    EditAIResponse: PropTypes.func.isRequired,
    resetIsAIResponseGenerated: PropTypes.func.isRequired,
    SendMessageAction: PropTypes.func.isRequired,
    addMessageToRedux: PropTypes.func.isRequired,
    fetchedIndividualAIResponse: PropTypes.object,
    phone_number: PropTypes.string.isRequired,
    backToMessageIntent: PropTypes.func.isRequired,
    closeShowAIResponse: PropTypes.func.isRequired,
    aiResponseGenerated: PropTypes.bool,
  };

  componentDidMount() {
    const { phone_number } = this.props;
    if (phone_number) {
      this.props.GetIndividualAIResponse(phone_number);
    }
  }

  componentDidUpdate(prevProps) {
    const { aiResponseGenerated, phone_number, resetIsAIResponseGenerated } = this.props;

    if (aiResponseGenerated && aiResponseGenerated !== prevProps.aiResponseGenerated) {
      this.props.GetIndividualAIResponse(phone_number);
      setTimeout(() => {
        resetIsAIResponseGenerated();
        this.setState({ isGeneratingResponse: false });
      }, 100);
    }
  }

  handleToneChange = (event) => {
    this.setState({ tone: event.target.value });
  };

  handleLengthChange = (event) => {
    this.setState({ length: event.target.value });
  };

  handleContentBlur = (event) => {
    const { phone_number } = this.props;
    const editedResponse = event.target.textContent;

    if (!editedResponse.trim()) {
      this.setState({ editError: "Response cannot be empty." });
      setTimeout(() => this.setState({ editError: null }), 3000);
      return;
    }

    this.props.EditAIResponse(phone_number, editedResponse);
    setTimeout(() => this.props.GetIndividualAIResponse(phone_number), 1000);
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const { phone_number } = this.props;
    const { tone, length } = this.state;

    this.props.GenerateAIResponse(phone_number, tone, length);
    this.setState({ isGeneratingResponse: true });
  };

  handleSend = () => {
    const { phone_number, fetchedIndividualAIResponse } = this.props;
    const response = fetchedIndividualAIResponse?.response;

    if (!response || !response.trim()) {
      this.setState({ sendError: "Cannot send an empty response." });
      setTimeout(() => this.setState({ sendError: null }), 3000);
      return;
    }

    this.props.SendMessageAction(phone_number, response);
  };

  render() {
    const {
      backToMessageIntent,
      closeShowAIResponse,
      phone_number,
      message,
      handleMessageSentSuccess,
    } = this.props;
    const { tone, length, isGeneratingResponse, editError, sendError } = this.state;

    const currentAIResponse =
      this.props.fetchedIndividualAIResponse?.response ||
      "No response. Please generate a response.";

    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50 p-4">
        <div className="flex flex-col bg-white w-full max-w-2xl rounded-2xl shadow-xl">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-primary-blue rounded-t-2xl">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  backToMessageIntent();
                  this.props.addMessageToRedux(message);
                }}
                className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                aria-label="Back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold text-sm">
                {phone_number?.slice(-2)}
              </div>
              <div className="text-white">
                <p className="font-SFPro font-semibold text-sm">{phone_number}</p>
                <p className="font-inter text-xs text-white/70">Customer</p>
              </div>
            </div>
            <button
              onClick={closeShowAIResponse}
              className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex flex-col gap-5 px-6 py-6">

            {/* Current AI Response */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                AI Response
              </p>
              <div
                contentEditable
                suppressContentEditableWarning
                onBlur={this.handleContentBlur}
                className="w-full text-sm text-gray-700 font-inter p-3 border border-gray-200 rounded-xl focus:outline-none focus:border-primary-blue transition-colors min-h-[80px]"
              >
                {currentAIResponse}
              </div>
              {editError && (
                <p className="text-xs text-red-500 mt-1">{editError}</p>
              )}
            </div>

            {/* Tone + Length selectors side by side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tone</span>
                <DropdownField
                  name="tone"
                  value={tone}
                  onChange={this.handleToneChange}
                  options={[
                    { value: "Friendly", label: "Friendly" },
                    { value: "Professional", label: "Professional" },
                    { value: "Neutral", label: "Neutral" },
                  ]}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Length</span>
                <DropdownField
                  name="length"
                  value={length}
                  onChange={this.handleLengthChange}
                  options={[
                    { value: "Short", label: "Short" },
                    { value: "Medium", label: "Medium" },
                    { value: "Long", label: "Long" },
                  ]}
                />
              </div>
            </div>

            {sendError && (
              <p className="text-xs text-red-500 -mt-2">{sendError}</p>
            )}

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={this.handleSubmit}
                disabled={isGeneratingResponse}
                className="flex items-center justify-center gap-2 flex-1 h-10 bg-primary-blue text-white text-sm font-inter font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {isGeneratingResponse ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {isGeneratingResponse ? "Generating…" : "Generate Response"}
              </button>
              <button
                onClick={() => {
                  this.handleSend();
                  handleMessageSentSuccess();
                }}
                className="flex items-center justify-center gap-2 flex-1 h-10 border border-primary-blue text-primary-blue text-sm font-inter font-semibold rounded-lg hover:bg-blue-50 transition-colors"
              >
                <Send className="w-4 h-4" />
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  fetchedIndividualAIResponse: state.messaging.fetchedIndividualAIResponse,
  aiResponseGenerated: state.messaging.aiResponseGenerated,
});

export default withRouterHooks(
  connect(mapStateToProps, {
    GetIndividualAIResponse,
    GenerateAIResponse,
    resetIsAIResponseGenerated,
    EditAIResponse,
    SendMessageAction,
    addMessageToRedux,
  })(CustomizeAIResponse)
);
